import type { AssigneeModel, ReassigneeType, StepSuccessors } from '@bliks-io/contracts';
import {
  Briefcase,
  CircleHelp,
  CircleOff,
  Contact,
  User as UserIcon,
  Users as UsersIcon,
  type LucideIcon
} from 'lucide-react';
import { pickFirstAssignee, type SuccessorCollectionKey } from '@bliks-io/ui';
import { ENTITY_ICONS } from '@/constants/entityIcons';

/**
 * The chooser/sidebar phases of the successor/assignee picker. Frontend-only:
 * `team` and `role` are two presentation entries over the ONE stored
 * `successors.orgFunctions[]` slot (ADR 0004) — both phases save the same
 * union-ref payload, they only differ in which kind their list shows.
 */
export type SuccessorChooserKey =
  | 'person'
  | 'team'
  | 'role'
  | 'system'
  | 'externalContact'
  | 'unclear'
  | 'none';

export interface SuccessorTypeOption {
  value: SuccessorChooserKey;
  icon: LucideIcon;
  labelKey: string;
  /** Long-form description; only rendered by the chooser, not the sidebar. */
  sublabelKey?: string;
}

export const SUCCESSOR_ENTITY_TYPES: ReadonlyArray<SuccessorTypeOption> = [
  {
    value: 'person',
    icon: UserIcon,
    labelKey: 'processPage.Person',
    sublabelKey: 'entityPicker.successor.type.person.sublabel'
  },
  {
    value: 'team',
    icon: UsersIcon,
    labelKey: 'organization.orgFunctions.kind.team',
    sublabelKey: 'entityPicker.successor.type.team.sublabel'
  },
  {
    value: 'role',
    icon: Briefcase,
    labelKey: 'organization.orgFunctions.kind.role',
    sublabelKey: 'entityPicker.successor.type.role.sublabel'
  },
  {
    value: 'system',
    icon: ENTITY_ICONS.system,
    labelKey: 'common.entityTypes.SoftwareSystem',
    sublabelKey: 'entityPicker.successor.type.system.sublabel'
  },
  {
    value: 'externalContact',
    icon: Contact,
    labelKey: 'common.entityTypes.ExternalContact',
    sublabelKey: 'entityPicker.successor.type.externalContact.sublabel'
  }
];

export const SUCCESSOR_STATUS_TYPES: ReadonlyArray<SuccessorTypeOption> = [
  {
    value: 'unclear',
    icon: CircleHelp,
    labelKey: 'processPage.Unclear',
    sublabelKey: 'entityPicker.successor.type.unclear.sublabel'
  },
  {
    value: 'none',
    icon: CircleOff,
    labelKey: 'processPage.None',
    sublabelKey: 'entityPicker.successor.type.none.sublabel'
  }
];

export const SUCCESSOR_MODEL_BY_TYPE: Record<SuccessorCollectionKey, AssigneeModel> = {
  persons: 'User',
  orgFunctions: 'OrgFunction',
  systems: 'System',
  externalContacts: 'ExternalContact'
};

export function filterSuccessorTypes(
  options: ReadonlyArray<SuccessorTypeOption>,
  hide: {
    hideExternalContact?: boolean;
    hideNone?: boolean;
    hideUnclear?: boolean;
  }
): SuccessorTypeOption[] {
  return options.filter((o) => {
    if (o.value === 'externalContact' && hide.hideExternalContact) return false;
    if (o.value === 'none' && hide.hideNone) return false;
    if (o.value === 'unclear' && hide.hideUnclear) return false;
    return true;
  });
}

/**
 * Maps a `SuccessorUpdateData` payload from `SuccessorEntityPickerModal` to
 * the `(assigneeId, assigneeModel)` pair the reassign / resolve-unclear /
 * draft-assignment APIs expect. Returns `null` for unclear / none payloads
 * — callers convert those to status-marker mutations instead.
 */
export interface ReassigneePair {
  assigneeId: string;
  assigneeModel: AssigneeModel;
  /**
   * `'suggestion'` when the picked OrgFunction/System is a still-pending
   * EntitySuggestion rather than a real entity — the reassign flow writes it as
   * a placeholder assignment that approval later promotes. Persons / external
   * contacts are always `'entity'` (no suggestion workflow for them).
   */
  type: ReassigneeType;
}

export function pickReassigneeFromSuccessors(data: StepSuccessors): ReassigneePair | null {
  // Shared first-match rule (`pickFirstAssignee`); the ref's own type carries
  // through, so a `suggestion` ref is staged as a placeholder assignment
  // instead of being dropped (which previously made the confirm button dead).
  const picked = pickFirstAssignee(data);
  if (!picked) return null;
  return { assigneeId: picked.ref.id, assigneeModel: picked.model, type: picked.ref.type };
}

export function isUnclearStatus(data: StepSuccessors): boolean {
  return data.successorStatus === 'UNCLEAR';
}

export interface ReassignPayload {
  reAssigneeId?: string;
  reAssigneeModel?: AssigneeModel;
  /**
   * `'suggestion'` when the reassignee is a proposed (not-yet-real) OrgFunction/System,
   * else `'entity'`. Always set so the request never relies on the contract
   * default; ignored by the backend on the unclear path (no reassignee).
   */
  reAssigneeType: ReassigneeType;
  isUnclear: boolean;
}

/**
 * Normalizes a picker `SuccessorUpdateData` into the `{ reAssigneeId,
 * reAssigneeModel, isUnclear }` shape the reassign / reject / resolve-unclear
 * APIs consume. Returns `null` when the payload selects neither an entity nor
 * the unclear status — caller should bail.
 */
export function toReassignPayload(data: StepSuccessors): ReassignPayload | null {
  if (isUnclearStatus(data)) return { isUnclear: true, reAssigneeType: 'entity' };
  const pair = pickReassigneeFromSuccessors(data);
  if (!pair) return null;
  return {
    reAssigneeId: pair.assigneeId,
    reAssigneeModel: pair.assigneeModel,
    reAssigneeType: pair.type,
    isUnclear: false
  };
}
